import type { SmsScene } from 'src/constants/global-constant';
import type { SignInSchemaType } from 'src/auth/schema/sign-in-schema';

import { encryptPhone } from 'src/utils/utils';

import API from 'src/services/API';
import axios, { endpoints } from 'src/lib/axios';

import { setSession } from './utils';

// ----------------------------------------------------------------------

/** **************************************
 * Sign in
 *
 * 调 nest-api `/api/v1/auth/sign-in`，成功后：
 *   - 把 accessToken / refreshToken 存到 localStorage
 *   - 返回登录响应中的 user，由调用方判断 isAdmin / isSuperAdmin / tenantId 决定跳转目标
 *
 * 注意：B 端后台只接受 isAdmin 或 isSuperAdmin 的账号；非管理员的判定放在 view 层，
 * 因为这里要先 setSession（否则后续 /auth/me 等调用拿不到 token）。
 *************************************** */
export const signIn = async (data: SignInSchemaType): Promise<MyApi.OutputAuthDto> => {
  try {
    const res = await axios.post<MyApi.ApiResponseOutputAuthDto>(endpoints.auth.signIn, data, {
      fetchOptions: {
        showSuccess: false,
      },
    });

    const result = res.data.data;
    const accessToken = result?.accessToken;
    const refreshToken = result?.refreshToken;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    setSession(accessToken, refreshToken);

    return result;
  } catch (error) {
    console.error('Error during sign in:', error);
    throw error;
  }
};

/** **************************************
 * Sign up
 *
 * B 端账号默认不开放公开注册（由总部 / 加盟商主账号开通）。
 * 此函数保留是为了照顾模板原有 sign-up 视图，实际生产路由可禁用。
 *************************************** */
export const signUp = async (params: MyApi.RegisterDto): Promise<MyApi.OutputAuthDto> => {
  try {
    const res = await API.Auth.AuthControllerSignUpV1(params);

    const result = res.data.data;
    const { accessToken, refreshToken } = result;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }
    setSession(accessToken, refreshToken);

    return result;
  } catch (error) {
    console.error('注册过程中发生错误：', error);
    throw error;
  }
};

/** **************************************
 * Sign out
 *
 * 仅清本地 token；nest-api 的 /auth/logout 仅记录日志，不影响 JWT 有效性。
 *************************************** */
export const signOut = async (): Promise<void> => {
  try {
    await setSession(null);
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
};

/** **************************************
 * Send Code
 *************************************** */
export const sendCode = async (params: {
  scene: SmsScene;
  phoneNumber: string;
  captchaCode: string;
}) =>
  API.Sms.SmsControllerSendCodeV1(params, {
    fetchOptions: {
      showError: true,
      showSuccess: true,
      successMessage: `验证码已发送至您${encryptPhone(params.phoneNumber)}的手机号上，请注意查收`,
    },
  });
